/**
 * MONSTER TOWN — ONE NEIGHBOUR. NPC CREW (src/npc/**).
 *
 * A neighbour is the same monster the kid made: same art builder, same rig, same
 * animator, same walker. That is deliberate — it makes the town feel like one
 * species, and it means every improvement to the kid's monster improves all of
 * them for free.
 *
 * NPCs are ACTORS, never placements: they live in `node.actors`, are never
 * written into the grid, and never appear in the placements save section. Their
 * roster is saved separately so a kid comes back to the same faces.
 */

import type * as THREE from 'three'
import { engine } from '../core/engine'
import type { Grid } from '../core/grid'
import type { SceneNode } from '../core/scene'
import type { MonsterConfig, NpcId, NpcMood, NpcState, SceneId, Vec3 } from '../core/types'
import { buildMonster, facingBiasedToCamera, type MonsterRig } from '../monster/art'
import { MonsterAnimator } from '../monster/anim'
import { Walker } from '../monster/walker'
import type { RoutineActivity } from '../core/schedule'
import type { Archetype } from './roster'

export type NpcActivity =
  | 'idle'
  | 'wander'
  | 'admire'
  | 'visit'
  | 'dance'
  | 'nap'
  | 'sit'
  | 'greet'
  | 'customer'
  | 'gift'
  | 'leaving'

/** Neighbours amble. The kid's monster is always the quickest thing on screen. */
const NPC_SPEED = 2.6
/**
 * How far off its own heading an IDLE neighbour may be turned toward the lens.
 * 1.15 rad (66 degrees) is a three-quarter view; the rig's face turn covers the
 * rest, so a standing neighbour always has a readable face without ever
 * snapping square-on like a cut-out.
 */
const IDLE_FACE_LIMIT = 1.15

export class Npc {
  readonly id: NpcId
  readonly archetype: Archetype
  readonly name: string
  look: MonsterConfig
  sceneId: SceneId
  known: boolean
  mood: NpcMood = 'happy'

  activity: NpcActivity = 'idle'
  /** Seconds until the brain is allowed to pick something else. */
  timer = 0
  /** True while another crew (the venue) is steering them. */
  scripted = false
  /** What they walked over to look at, if anything. */
  focus: string | null = null
  /** An action to play the moment they arrive. */
  onArrive: string | null = null
  greetCooldown = 0

  // -- "A DAY IN TOWN" (additive) ----------------------------------------

  /**
   * What their schedule says they are doing, or null before the routine crew
   * has looked at them (and for ever, if their table id has no table). The brain
   * reads this to pick something that fits the hour; it never GATES anything.
   */
  routine: RoutineActivity | null = null
  /** The routine table this neighbour lives by. See `src/content/routines.ts`. */
  routineId: string | null = null
  /** Their own front step. Deterministic, walkable, and where they go to bed. */
  homeSpot: Vec3 | null = null
  /**
   * THEY WENT INSIDE. Not a state machine, not a scene change — the art is
   * simply hidden, which is what makes the street visibly empty at night.
   * Separate from `setVisible()` so the monster creator can hide the whole town
   * and give it back without waking anybody up.
   */
  indoors = false
  private stageVisible = true

  readonly walker = new Walker({ maxSpeed: NPC_SPEED, arriveDist: 0.35 })
  private rig: MonsterRig
  private anim: MonsterAnimator
  private attachedTo: THREE.Group | null = null
  private scale: number

  constructor(
    id: NpcId,
    archetype: Archetype,
    look: MonsterConfig,
    sceneId: SceneId,
    known: boolean,
    scale = 1,
  ) {
    this.id = id
    this.archetype = archetype
    this.name = archetype.name
    this.look = look
    this.sceneId = sceneId
    this.known = known
    this.scale = scale
    // 'simple' detail buys neighbours cheaper spheres, NOT a cheaper face. The
    // round-2 critic read the old tiering as the brief's named failure —
    // "monsters that read as props" — because a neighbour with no mouth and no
    // outline standing beside the kid's own monster is what makes the game look
    // unfinished, and it is always standing beside the kid's own monster.
    this.rig = buildMonster(look, { detail: 'simple', scale })
    this.anim = new MonsterAnimator(this.rig, engine.rng)
  }

  /** Neighbours are drawn small in a doll-house room and big out in the town. */
  setScale(scale: number): void {
    if (Math.abs(scale - this.scale) < 0.001) return
    this.scale = scale
    this.reskin(this.look)
  }

  /** Hidden while the creator is open: that screen is about the kid's monster. */
  setVisible(on: boolean): void {
    this.stageVisible = on
    this.rig.root.visible = on && !this.indoors
  }

  /**
   * Gone in for the night (or come back out). Kept apart from `setVisible` on
   * purpose: closing the monster creator used to hand visibility back to every
   * neighbour in town, including the seven who were supposed to be asleep.
   */
  setIndoors(on: boolean): void {
    this.indoors = on
    this.rig.root.visible = this.stageVisible && !on
  }

  get position(): Vec3 {
    return { x: this.walker.x, y: 0, z: this.walker.z }
  }

  get height(): number {
    return this.rig.height
  }

  setPosition(x: number, z: number): void {
    this.walker.setPosition(x, z)
    this.rig.root.position.set(x, 0, z)
    // Placed, not turned: start the eased facing where the walker already is,
    // or a neighbour dropped into a scene spins to its heading on arrival.
    this.shownFacing = this.walker.facing
  }

  /** Move this neighbour's art into a scene's actor layer. */
  attach(node: SceneNode): void {
    if (this.attachedTo === node.actors) return
    this.rig.root.parent?.remove(this.rig.root)
    node.actors.add(this.rig.root)
    this.attachedTo = node.actors
    this.sceneId = node.def.id
  }

  detach(): void {
    this.rig.root.parent?.remove(this.rig.root)
    this.attachedTo = null
  }

  /** Rebuild the art after a look change (a new hat, a mood makeover). */
  reskin(look: MonsterConfig): void {
    const parent = this.rig.root.parent
    this.rig.root.parent?.remove(this.rig.root)
    this.look = look
    this.rig = buildMonster(look, { detail: 'simple', scale: this.scale })
    this.anim = new MonsterAnimator(this.rig, engine.rng)
    this.rig.root.position.set(this.walker.x, 0, this.walker.z)
    this.rig.root.rotation.y = this.shownFacing
    // A fresh rig is visible by default; a neighbour who was indoors when their
    // art was rebuilt must not pop back onto the street.
    this.rig.root.visible = this.stageVisible && !this.indoors
    parent?.add(this.rig.root)
  }

  walkTo(p: Vec3, then: string | null = null, stopDist = 0.35): void {
    this.walker.walkTo(p.x, p.z, stopDist)
    this.onArrive = then
    this.anim.stop()
  }

  act(action: string, seconds?: number): void {
    this.anim.play(action, seconds)
  }

  /** Ease out of a held pose (dance, nap, sit). */
  stopActing(): void {
    this.anim.stop()
  }

  faceTowards(p: Vec3): void {
    this.walker.faceTowards(p.x, p.z)
  }

  get busyActing(): boolean {
    return this.anim.busy
  }

  get walking(): boolean {
    return this.walker.walking
  }

  /** Fixed-step movement. Returns true on the frame they arrive. */
  step(dt: number, grid: Grid | null): boolean {
    this.timer = Math.max(0, this.timer - dt)
    this.greetCooldown = Math.max(0, this.greetCooldown - dt)
    const arrived = this.walker.step(dt, grid)
    if (arrived && this.onArrive !== null) {
      this.anim.play(this.onArrive)
      this.onArrive = null
    }
    return arrived
  }

  /**
   * A NEIGHBOUR STANDING STILL LOOKS UP.
   *
   * Walking, they face where they are going — that is what makes the town read
   * as alive. Standing, they kept the yaw they stopped on, which for half the
   * town is squarely away from the lens: the critic's frames show the
   * neighbours as "red sphere-on-cone chess pawns" and "a headless sphere with
   * one stub foot", which is the back of a monster doing nothing wrong.
   *
   * So an idle neighbour eases round toward the camera, no further than
   * `IDLE_FACE_LIMIT` off whatever they were doing, and the rig's own face turn
   * takes the rest. It is what a neighbour would do when someone walks past.
   */
  private shownFacing = 0

  /** Per-render-frame pose update. */
  render(dtSeconds: number): void {
    this.rig.root.position.set(this.walker.x, 0, this.walker.z)
    const want = this.walker.walking
      ? this.walker.facing
      : facingBiasedToCamera(this.walker.facing, IDLE_FACE_LIMIT)
    // Ease, never snap: a neighbour that pivots the instant it stops walking
    // reads as a cardboard stand-up turning to the camera.
    let d = want - this.shownFacing
    while (d > Math.PI) d -= Math.PI * 2
    while (d < -Math.PI) d += Math.PI * 2
    this.shownFacing += d * Math.min(1, Math.max(0, dtSeconds) * 6)
    this.rig.root.rotation.y = this.shownFacing
    this.anim.setSpeed(this.walker.gait)
    this.anim.update(dtSeconds)
  }

  toState(): NpcState {
    return {
      id: this.id,
      look: this.look,
      name: this.name,
      sceneId: this.sceneId,
      pos: this.position,
      mood: this.mood,
      activity: this.activity,
      known: this.known,
    }
  }
}
